import { useConnection, useWallet } from "@solana/wallet-adapter-react";
import { WalletMultiButton } from "@solana/wallet-adapter-react-ui";
import { motion } from "framer-motion";
import { DiamondIcon, ShieldCheckIcon, ShieldIcon, Star } from "lucide-react";
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import ContentPanel from "../components/shop/ContentPanel";
import ComingSoon from "../components/ComingSoon";
import { getAllPrices } from "../lib/api";
import { fetchCNFTsInWallet } from "../lib/cnft";
import { CollectionType, NftDataType, PriceType } from "../lib/types";

const Home = () => {
  const showComingSoon = true; // Set to false to enable the site

  if (showComingSoon) {
    return <ComingSoon />;
  }

  const { connection } = useConnection();
  const { publicKey, wallet } = useWallet();
  const [pageLoading, setPageLoading] = useState(false);

  // ! Project NFTs

  const [cNFTs, setCNFTs] = useState<NftDataType[] | []>([]);
  const [selectedNFT, setSelectedNFT] = useState<NftDataType | undefined>(
    undefined
  );
  const [prices, setPrices] = useState<PriceType[]>([]);
  const [collections, setCollections] = useState<CollectionType[]>([]);
  const [nftLoading, setNftLoading] = useState(false);
  const [selectedTraitType, setSelectedTraitType] = useState("Select a Trait Type");
  const [collectionSelected, setCollectionSelected] = useState<
    CollectionType | undefined
  >(undefined);

  const selectedTraitTypeChange = (value: string) => {
    setSelectedTraitType(value);
  };

  const getPrices = async () => {
    try {
      const response = await getAllPrices();
      //console.log("Raw API Response:", response);

      if (!response || typeof response !== "object") {
        console.error("Invalid response format");
        return;
      }

      const pricesArray = response || [];
      //console.log("Parsed Prices:", pricesArray);

      if (!Array.isArray(pricesArray) || pricesArray.length === 0) {
        console.error("No Prices to Fetch: Empty or invalid prices array");
        return;
      }

      setPrices(pricesArray);
    } catch (error) {
      console.error("Error fetching prices:", error);
    }
  };



  const features = [
    {
      label: 'Upgrade System',
      icon: ShieldIcon,
      color: 'cyan',
      description: 'An evolving upgrade system for Boobies NFTs that safeguards uniqueness and rewards smart collecting. Customize your flock with intention'
    },
    {
      label: 'Dynamic Rarity Engine',
      icon: DiamondIcon,
      color: 'purple',
      description: 'The Plume Protocol maintains collection diversity and rarity integrity, preventing common floor NFT issues.'
    },
    {
      label: 'Exclusive Access',
      icon: Star,
      color: 'amber',
      description: 'Members-only drops, curated trait bundles, and early access to premium features.'
    }
  ];

  const traitTypes = useMemo(() => {
    try {
      const _traitTypes: string[] = [];

      if (!prices || !Array.isArray(prices)) {
        console.error("Prices is not an array or is undefined:", prices);
        return [];
      }

      for (const price of prices) {
        if (
          price?.trait?.trait_type &&
          !_traitTypes.includes(price.trait.trait_type)
        ) {
          _traitTypes.push(price.trait.trait_type);
        }
      }

      return _traitTypes;
    } catch (error) {
      console.error("Error in traitTypes memo:", error);
      //console.log("prices:", prices);
      return [];
    }
  }, [prices]);


  const fetchWalletNfts = useCallback(async () => {
    if (!publicKey) return;
    try {
      setNftLoading(true);
      setSelectedNFT(undefined);

      const cnfts = await fetchCNFTsInWallet(publicKey);
      setCNFTs(cnfts);
    } catch (e) {
      console.log("fetching NFTs failed in use-memo ", e);
    } finally {
      setNftLoading(false);
    }
  }, [publicKey]);

  const carouselRef = useRef(null);
  useEffect(() => {
    if (!publicKey) {
      setCollectionSelected(undefined);
      return;
    }
  });

  useEffect(() => {
    if (publicKey) {
      fetchWalletNfts();
    }
  }, [publicKey, fetchWalletNfts]);

  useEffect(() => {
    if (!publicKey) {
      setCollectionSelected(undefined);
      return;
    }

    setPageLoading(true);
    setCollections(collections);
    const fetchData = async () => {
      try {
        await fetchWalletNfts();
        await getPrices();
        // await getCollections();
      } catch (error) {
        console.error("Error during data fetching:", error);
      } finally {
        setPageLoading(false);
      }
    };

    fetchData();
    return () => { };
  }, [publicKey]);


  return (
    <div className="min-h-screen bg-black text-white selection:bg-brand-purple selection:text-white relative overflow-hidden font-sans">
      {/* Background Effects */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#4f4f4f2e_1px,transparent_1px),linear-gradient(to_bottom,#4f4f4f2e_1px,transparent_1px)] bg-[size:14px_24px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]" />
        <div className="absolute top-0 left-0 right-0 h-[500px] bg-brand-purple/20 blur-[100px] rounded-full mix-blend-screen opacity-30" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-brand-cyan/10 blur-[120px] rounded-full mix-blend-screen opacity-20" />
      </div>

      <div className="relative z-10">
        {!publicKey && (
          <div className="flex flex-col items-center justify-center min-h-screen px-4 py-20">
            
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="w-full max-w-2xl"
            >
              <div className="glass-panel p-8 md:p-12 rounded-3xl border border-white/10 relative overflow-hidden group">
                {/* Cyber scanline effect */}
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-brand-cyan/5 to-transparent -translate-y-full group-hover:translate-y-full transition-transform duration-1000 ease-in-out" />
                
                <div className="flex flex-col items-center gap-8 relative z-10">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2, type: "spring" }}
                    className="text-center"
                  >
                    <h1 className="text-5xl md:text-6xl font-black tracking-tighter mb-2">
                      <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-cyan via-brand-purple to-brand-pink">
                        Plume Protocol
                      </span>
                    </h1>
                    <div className="h-1 w-24 mx-auto bg-gradient-to-r from-brand-cyan to-brand-purple rounded-full" />
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.4 }}
                    className="text-center max-w-lg"
                  >
                    <p className="text-lg text-gray-300 leading-relaxed font-medium">
                      Unlock exclusive digital traits and curated NFT experiences. Connect your wallet to explore rare collections, activate upgrades, and elevate your Boobies.
                    </p>
                  </motion.div>

                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="w-full max-w-xs"
                  >
                    <div className="p-[1px] rounded-xl bg-gradient-to-r from-brand-cyan via-brand-purple to-brand-pink shadow-lg shadow-brand-purple/20">
                      <div className="bg-black/90 rounded-[11px] backdrop-blur-xl">
                        <WalletMultiButton className="!bg-transparent !h-14 !w-full !justify-center !text-lg !font-bold !text-white hover:!bg-white/5 !transition-all !rounded-xl" />
                      </div>
                    </div>
                  </motion.div>
                </div>
              </div>
            </motion.div>

            {/* Feature highlights */}
            <div className="mt-24 grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl w-full">
              {features.map(({ label, icon: Icon, color, description }, index) => (
                <motion.div
                  key={label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 + index * 0.1 }}
                  whileHover={{ y: -5 }}
                  className="h-full"
                >
                  <div className="glass-panel p-6 rounded-2xl h-full border border-white/5 hover:border-brand-purple/30 transition-all duration-300 group">
                    <div className="flex flex-col items-center text-center gap-4 h-full">
                      <div className={`p-4 rounded-xl bg-gradient-to-br from-${color}-500/20 to-transparent border border-${color}-500/20 group-hover:scale-110 transition-transform duration-300`}>
                        <Icon className={`w-8 h-8 text-${color}-400`} />
                      </div>
                      
                      <h3 className="text-xl font-bold text-white group-hover:text-brand-cyan transition-colors">
                        {label}
                      </h3>
                      
                      <p className="text-sm text-gray-400 leading-relaxed">
                        {description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {publicKey && (
          <div className="pt-24 px-4 pb-12 max-w-[1600px] mx-auto">
            <ContentPanel
              prices={prices}
              traitTypes={traitTypes}
              selectedTraitType={selectedTraitType}
              AllFilteredNFTs={cNFTs}
              selectedNft={selectedNFT}
              setCollectionSelected={setCollectionSelected}
              selectedCollection={collectionSelected!}
              selectedTraitTypeChange={selectedTraitTypeChange}
              setSelectedNft={setSelectedNFT}
              setPageLoading={setPageLoading}
              nftLoading={nftLoading}
              fetchWalletNfts={fetchWalletNfts}
              getPrices={getPrices}
              carouselRef={carouselRef}
            />
          </div>
        )}

        {/* Global Loading State */}
        {pageLoading && (
          <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-xl flex flex-col items-center justify-center gap-6">
            <div className="relative">
              <div className="w-16 h-16 rounded-full border-4 border-brand-purple/30 border-t-brand-cyan animate-spin" />
              <div className="absolute inset-0 rounded-full border-4 border-transparent border-b-brand-pink/50 animate-spin-reverse" />
            </div>
            <p className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-brand-cyan to-brand-purple animate-pulse">
              Curating Your Experience...
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Home;
